import React from 'react'
import { type SurveyCompletionRate } from '../../models/Division'
import { SectionTableFeature } from './components/SectionTable'
import { TopLevelSurveyResult } from './components/TopLevelSurveyResult'
import SurveyReportItems from './components/SurveyReportItems'
import { type ReportsV2 } from '../../models/Report'
import { SurveyScoreChart } from './components/SurveyScoreChart'
import { useReportsContext } from './ReportsContext'
import EmptyListCard from '../../features/InstructorDashboardFeature/components/EmptyListCard'
import { Box, Card, CardContent, CircularProgress, Typography, useTheme } from '@mui/material'
import { grey } from '../../colors'

export interface ReportsFeatureProps {
  completionRate: SurveyCompletionRate | null
  reports: ReportsV2
  reportError: Record<string, any> | null
  parentSurveyScore?: number | null
  isReportsPending?: boolean
  isCompletionRateError?: boolean
  seriesId?: string | null
  termId?: string | null
}

const ReportsFeature: React.FC<ReportsFeatureProps> = (props) => {
  const {
    completionRate,
    reports,
    reportError,
    parentSurveyScore,
    isReportsPending = false,
    isCompletionRateError = false,
    seriesId,
    termId
  } = props
  const showSectionTable = completionRate?.categoryName && String(completionRate.categoryName) !== 'Section'
  const { selectedTrend } = useReportsContext()
  const showSurveyScoreChart = selectedTrend > 1 && reports?.surveyScores
  const theme = useTheme()

  const renderReports = (): JSX.Element => {
    if (isReportsPending) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
          <CircularProgress />
        </Box>
      )
    }
    if (!reports || reportError) {
      const isCollectingData = reportError?.message?.earlyAccess_1ReleaseDate === null && reportError?.message?.earlyAccess_2ReleaseDate === null
      return (
        <Card>
          <Box sx={{ p: `${theme.spacing(8)} ${theme.spacing(6)}` }}>
            {isCollectingData
              ? <Typography color={grey[850]} >
                Results for this survey are currently being collected.
              </Typography>
              : <Typography component={'span'} fontWeight={500} color={grey[250]}>
                {reportError?.message}
              </Typography>
            }
          </Box>
        </Card>
      )
    }
    if (reports?.surveySections?.length && !reportError && completionRate && seriesId && termId) {
      return (
        <SurveyReportItems
          reports={reports}
          completionRate={completionRate}
          reportError={reportError}
        />
      )
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        {/* TODO: Display an empty state component (e.g., <EmptyListCard />) if no survey reports are available */}
        {/* <EmptyListCard /> */}
      </Box>
    )
  }

  const renderNoProjectOrTerm = (): JSX.Element => (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%" >
      <Card sx={{ p: 4 }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            To see Reports, select a Survey Project and Term.
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Use the dropdowns above to choose a project and term to view results.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )

  const renderMainReport = (): JSX.Element => (
    <>
      {showSectionTable && <SectionTableFeature completionRate={completionRate} parentSurveyScore={parentSurveyScore} />}
      <TopLevelSurveyResult calculations={reports?.calculations} titles={reports?.titles} />
      {showSurveyScoreChart && <SurveyScoreChart reports={reports} />}
      {renderReports()}
    </>
  )

  if (!seriesId || !termId) {
    return renderNoProjectOrTerm()
  }

  if (completionRate && !isCompletionRateError) {
    return renderMainReport()
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <EmptyListCard />
    </Box>
  )
}

export default ReportsFeature
